import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  Divider,
  Modal,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip, // Ensure Tooltip is imported
  AlertTitle, // For better error display
  Stack,
  Dialog,
  Slide,
  DialogTitle,
  DialogContent, // Useful for button layout
} from "@mui/material";
import ArrowBackSharpIcon from "@mui/icons-material/ArrowBackSharp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from '@mui/icons-material/Description'; // Icon for file path
import PageviewIcon from '@mui/icons-material/Pageview'; // Icon for results title
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HOST_URL from "../../../utils/Url"; // Ensure this path is correct
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp"; // Ensure this path is correct
import Autocomplete from "@mui/material/Autocomplete";
import nopdf from '../../../assets/images/nopdf.png'; // Ensure this path is correct
import { TransitionProps } from "@mui/material/transitions";
import { getSecureSubDeptId } from "../../../utils/storageHelpers";

// Define the expected structure of the API response data
interface PdfResultData {
  sNo: number;
  pageSize: number;
  pdfName: string;
  filePath: string;
  pdfBase64: string;
}

// Style for the Modal - Slightly adjusted padding and max width
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PdfCounter: React.FC = () => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [optionFileName, setOptionFileName] = useState<Array<{ value: string; label: string; path: string }>>([
    { value: "-1", label: t("text.SelectPdf"), path: "" },
  ]);
  const [pdfResult, setPdfResult] = useState<PdfResultData | null>(null);
  const [isCounting, setIsCounting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // No need for selectedFileLabel state, derive from formik.values or pdfResult

  // --- Modal State ---
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const handlePreviewOpen = () => setIsPreviewModalOpen(true);
  const handlePreviewClose = () => setIsPreviewModalOpen(false);

  const subDeptID = getSecureSubDeptId();
  // --- End Modal State ---

  useEffect(() => {
    getCheckModeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Add t to dependency array if translations load async affecting the initial label

  const getCheckModeData = () => {
    const collectData = { pdFid: -1, user_id: 0 };
    axios.post<{ data: Array<{ pdfPath: string; pdfName: string; pdFid: number }> }>(
      HOST_URL.HOST_URL2 + "DocFiles/GetDocFiles", collectData
    )
      .then((res) => {
        if (res.data?.data && Array.isArray(res.data.data)) {
          const arr = res.data.data.map(item => ({
            path: item.pdfPath,
            label: item.pdfName || 'Unnamed File', // Handle potentially missing names
            value: String(item.pdFid),
          }));
          // Filter out invalid entries if necessary (e.g., missing path)
          const validArr = arr.filter(item => item.path);
          setOptionFileName([{ value: "-1", label: t("text.SelectPdf"), path: "" }, ...validArr]);
        } else {
          console.warn("No valid data received from GetDocFiles or data format is incorrect.");
          setOptionFileName([{ value: "-1", label: t("text.SelectPdf"), path: "" }]);
          // Optionally show a toast warning here
          // toast.warn(t('text.WarningFetchingFileList'));
        }
      })
      .catch(error => {
        console.error("Error fetching file list:", error);
        setOptionFileName([{ value: "-1", label: t("text.SelectPdf"), path: "" }]);
        toast.error(t('text.ErrorFetchingFileList'));
      });
  };

  const formik = useFormik<{
    // sNo and pageSize are results, not inputs for this form
    pdfName: string; // Keep track of the selected name for display consistency
    filePath: string;
  }>({
    initialValues: {
      pdfName: "",
      filePath: "",
    },
    validationSchema: Yup.object({
      // pdfName is derived, filePath is the key input to validate
      filePath: Yup.string().required(t('text.FilePathRequired') || 'Please select a file'),
    }),
    onSubmit: async (values) => {
      if (!values.filePath) { // Double check, though validation should catch it
        toast.warn('Please select a file first');
        return;
      }
      setIsCounting(true);
      setApiError(null);
      setPdfResult(null); // Clear previous results
      handlePreviewClose(); // Close modal if open

      const payload = { filePath: values.filePath };

      try {
        const response = await axios.post<{ status: number; message: string; data: PdfResultData }>(
          HOST_URL.HOST_URL2 + `SplitPdf/GetPageCountOfPdf`,
          payload
        );
        if (response.data?.status === 1 && response.data.data) {
          // Add base64 prefix if missing (assuming it's just the raw base64 data)
          let base64Data = response.data.data.pdfBase64 || '';
          if (base64Data && !base64Data.startsWith('data:application/pdf;base64,')) {
            base64Data = 'data:application/pdf;base64,' + base64Data;
          }
          setPdfResult({ ...response.data.data, pdfBase64: base64Data });
          toast.success(response.data.message);
        } else {
          const errorMsg = response.data?.message;
          setApiError(errorMsg);
          toast.error(errorMsg);
        }
      } catch (error: any) {
        console.error("API Call failed:", error);
        const errorMsg = error.response?.data?.message || error.message;
        setApiError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setIsCounting(false);
      }
    },
  });

  const handleClear = () => {
    formik.resetForm();
    setPdfResult(null);
    setApiError(null);
    // Clear the Autocomplete visually
    // Since value is derived from formik.values.filePath, resetting formik works.
    // If you were controlling Autocomplete's value prop directly with state, you'd clear that state here.
    handlePreviewClose(); // Close modal on clear
  };

  // Find the selected option based on the formik value for the Autocomplete display
  const selectedOption = optionFileName.find(option => option.path === formik.values.filePath) || null;

  // Check if preview is possible
  //const canPreview = pdfResult?.pdfBase64 && pdfResult.pdfBase64.startsWith('data:application/pdf;base64,') && pdfResult.pdfBase64.length > 30; // Basic check
  const canPreview = Boolean(pdfResult?.pdfBase64 || pdfResult?.filePath);

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '1200px', margin: 'auto' }}> {/* Add overall padding and max-width */}
      <ToastApp />

      {/* --- Input Card --- */}
      <Card
        elevation={2} // Softer elevation
        sx={{
          mb: 4, // Increased margin bottom
          backgroundColor: "background.paper", // Use theme paper color
          borderRadius: '16px', // More rounded corners
          overflow: 'hidden', // Ensure content fits
          border: "1px solid",
          borderColor: "divider", // Use theme divider color
        }}
      >
        <Box // Header Box for better control
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5, // Padding for header
            // Example background using theme (optional)
            // backgroundColor: theme => alpha(theme.palette.primary.main, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tooltip title={t('text.Back') || 'Back'}>
            <IconButton
              onClick={() => navigate(-1)}
              size="medium" // Standard size
              sx={{
                // Example styling - adjust as needed
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              aria-label={t('text.Back') || 'Back'}
            >
              <ArrowBackSharpIcon />
            </IconButton>
          </Tooltip>

          <Typography
            variant="h5"
            component="h1" // Semantic heading
            textAlign="center"
            sx={{ fontWeight: 600, color: 'primary.main', flexGrow: 1 }} // Let title take available space
          >
            {t("text.PdfCounter")}
          </Typography>

          {/* Invisible placeholder for balance */}
          <Box sx={{ width: 40, height: 40, visibility: 'hidden' }} />
        </Box>


        <CardContent sx={{ p: { xs: 2, md: 3.5 } }}> {/* Responsive padding */}

          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3} alignItems="flex-start"> {/* Align items top */}

              {/* Autocomplete */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  disablePortal
                  id="pdf-file-select"
                  options={optionFileName.filter(option => option.value !== "-1")} // Exclude placeholder
                  getOptionLabel={(option) => option.label || ""}
                  value={selectedOption} // Control value display
                  isOptionEqualToValue={(option, value) => option.path === value?.path}
                  onChange={(_, newValue) => {
                    formik.setFieldValue("filePath", newValue?.path || "");
                    formik.setFieldValue("pdfName", newValue?.label || "");
                    // Clear results when selection changes
                    setPdfResult(null);
                    setApiError(null);
                    handlePreviewClose();
                  }}
                  fullWidth
                  size="medium" // Standard size
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("text.SelectPdf")}
                      variant="outlined"
                      required // Match validation
                      error={formik.touched.filePath && Boolean(formik.errors.filePath)} // Error based on filePath
                      helperText={formik.touched.filePath && formik.errors.filePath}
                      InputLabelProps={{ shrink: true }} // Keep label floated
                    />
                  )}
                  sx={{ mb: { xs: 2, md: 0 } }} // Margin bottom on mobile
                />
              </Grid>

              {/* Selected File Path */}
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("text.SelectedFilePath")}
                  value={formik.values.filePath || t("text.NoFileSelected") || 'No file selected'}
                  size="medium"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <DescriptionIcon sx={{ color: 'action.active', mr: 1 }} />
                    )
                  }}
                  sx={{
                    backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#333', // Subtle background
                    '& .MuiInputBase-input.Mui-disabled': { // Style readOnly as if disabled but clear
                      WebkitTextFillColor: 'text.secondary',
                      cursor: 'default',
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'action.disabledBackground', // Lighter border
                      },
                      '&:hover fieldset': {
                        borderColor: 'action.disabled', // Slightly darker on hover
                      },
                    },
                  }}
                // No error display needed here as it's derived from selection
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} sx={{ mt: 3 }}> {/* Increased top margin */}
                <Stack // Use Stack for easier button layout and spacing
                  direction={{ xs: 'column', sm: 'row' }} // Stack vertically on small screens
                  spacing={2}
                  justifyContent="center"
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large" // Larger buttons
                    sx={{
                      borderRadius: '24px', // Pill shape
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4, // More padding
                      minWidth: '180px', // Ensure min width
                    }}
                    startIcon={isCounting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isCounting ? t('text.Counting') : t("text.CountPages")}
                  </Button>

                  <Button
                    type="button"
                    variant="outlined"
                    color="secondary" // Use secondary color for contrast
                    onClick={handleClear}
                    disabled={isCounting} // Only disable while counting
                    size="large"
                    sx={{
                      borderRadius: '24px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 4,
                      minWidth: '180px',
                      borderColor: 'secondary.main', // Ensure border color matches text
                      '&:hover': {
                        backgroundColor: theme => theme.palette.action.hover, // Subtle background hover
                        borderColor: 'secondary.dark', // Darken border on hover
                      },
                    }}
                  >
                    {t("text.Clear")}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      {/* --- End Input Card --- */}


      {/* --- Result Display Area --- */}
      {(pdfResult || apiError) && (
        <Card
          elevation={2}
          sx={{
            backgroundColor: "background.paper",
            borderRadius: '16px',
            border: "1px solid",
            borderColor: "divider",
            overflow: 'hidden', // Clip content if needed
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', backgroundColor: theme => theme.palette.mode === 'light' ? 'grey[50]' : 'grey[800]' }}>
            <PageviewIcon color="primary" sx={{ mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('text.Results')}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {apiError && (
              <Alert severity="error" variant="standard" sx={{ mb: 2 }}> {/* Use standard variant */}
                <AlertTitle>{t('text.Error') || 'Error'}</AlertTitle>
                {apiError}
              </Alert>
            )}

            {pdfResult && !apiError && (
              <Grid container spacing={3} alignItems="center">
                {/* File Name & Preview */}
                <Grid item xs={12} md={8}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                    {t('text.FileName')}:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-all' }}>
                      {pdfResult.pdfName || t('text.UnnamedFile') || 'Unnamed File'}
                    </Typography>
                    {/* Preview Icon Button */}
                    {canPreview && (
                      <Tooltip title={t('text.Preview') || 'Preview PDF'}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={t('text.Preview') || 'Preview'}
                          onClick={() => {
                            if (pdfResult?.pdfBase64) {
                              // open modal preview
                              handlePreviewOpen();
                            } else if (pdfResult?.filePath) {
                              // open public URL in new tab
                              window.open(pdfResult.filePath, "_blank", "noopener,noreferrer");
                            }
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                  </Box>
                </Grid>

                {/* Page Count (takes remaining space on md+, stacks below on xs) */}
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                    {t('text.PageCount')}:
                  </Typography>
                  <Typography variant="h2" component="div" color="primary" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                    {pdfResult.pageSize}
                  </Typography>
                </Grid>


                {/* File Path (Optional, less prominent) */}
                <Grid item xs={12} sx={{ mt: 2, borderTop: '1px dashed', borderColor: 'divider', pt: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', wordBreak: 'break-all' }}>
                    <DescriptionIcon fontSize="inherit" sx={{ mr: 0.5, opacity: 0.7 }} />
                    {t('text.FilePath')}:&nbsp;
                    <Box component="span" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {pdfResult.filePath}
                    </Box>
                  </Typography>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
      {/* --- End Result Display Area --- */}


      {/* --- PDF Preview Modal --- */}
      <Dialog
        open={isPreviewModalOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handlePreviewClose}
        maxWidth="xl" // Use XL for PDFs
        fullWidth
        aria-labelledby="pdf-dialog-title"
        PaperProps={{ sx: { height: '90vh' } }} // Control dialog paper height
      >
        <DialogTitle sx={{ py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}> {/* Adjust padding & add border */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {/* Truncate title if too long */}
            <Typography variant="h6" component="div" noWrap sx={{ maxWidth: 'calc(100% - 50px)' }}>
              {pdfResult?.pdfName || ''}
            </Typography>
            <IconButton aria-label="close" onClick={handlePreviewClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden', flexGrow: 1 }}> {/* Remove padding, allow content to grow */}
          {pdfResult ? (
            <iframe
              src={`${pdfResult?.pdfBase64}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title={t('text.PdfPreview') || "PDF Preview"}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="textSecondary">{t("text.NoPDFPreviewAvailable")}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      {/* --- End PDF Preview Modal --- */}

    </Box>
  );
};

export default PdfCounter;